import {useEffect, useState} from 'react';
import type {ProductCatalogItem, SalesDynamics} from '@ozon-ai/ozon/contracts';
import {api} from '../../../api/client';
import {usePeriodPicker, periodSelectionKey} from '../../../components/period-picker/use-period-picker';
import {usePeriodPresets} from '../../../components/period-picker/use-period-presets';

export function useSalesDynamicsPage() {
	const {presets, primaryPresetId, loading: presetsLoading} = usePeriodPresets();
	const period = usePeriodPicker({
		presets,
		primaryPresetId,
		presetsReady: !presetsLoading,
	});
	const [products, setProducts] = useState<ProductCatalogItem[] | null>(null);
	const [sku, setSku] = useState('');
	const [row, setRow] = useState<SalesDynamics | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const selectionKey = periodSelectionKey(period.selection);

	useEffect(() => {
		let cancelled = false;
		void api
			.get<ProductCatalogItem[]>('/products/catalog')
			.then((items) => {
				if (cancelled) return;
				const withSku = items
					.filter((item) => item.ozonSku)
					.sort((left, right) => left.name.localeCompare(right.name, 'ru'));
				setProducts(withSku);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setProducts([]);
					setError(err instanceof Error ? err.message : 'Не удалось загрузить каталог');
				}
			});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (!sku) {
			setRow(null);
			setLoading(false);
			return;
		}
		if (!period.selection) {
			setRow(null);
			setError(period.localError);
			setLoading(false);
			return;
		}

		let cancelled = false;
		setLoading(true);
		setError(null);
		void api
			.post<SalesDynamics>('/sales-dynamics', {sku, period: period.selection})
			.then((next) => {
				if (!cancelled) setRow(next);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setRow(null);
					setError(err instanceof Error ? err.message : 'Не удалось посчитать динамику продаж');
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [period.localError, period.selection, selectionKey, sku]);

	return {
		period,
		products,
		sku,
		setSku,
		row,
		loading,
		error,
	};
}
